/**
 * SSE Progress Message Component
 * 
 * Displays real-time progress for SSE events, including server-owned tool calls.
 * Provides immediate visual feedback when server-side activities occur instead of showing generic loading dots.
 * 
 * Features:
 * - Immediate display when SSE events are received
 * - Color-coded status indicators for streaming server activity
 * - Left-aligned display (all SSE events are server-side)
 * - Expandable interface showing detailed event information
 * - Persistent display (no auto-dismiss) for continued visibility
 * - Real-time updates as new event data is received
 * - Neutral terminal styling for tool work stopped by the user
 * 
 * Event Types:
 * - tool_call: Shows server tool execution progress from new-chat tool.call/tool.result events
 * - progress: Generic progress indicator for other SSE events
 * 
 * Visual Design:
 * - Consistent color scheme and compact layout for chat activity
 * - No execution badges needed (all SSE events are server-side by nature)
 * - Smooth animations and transitions for professional appearance
 */

import React, { useState } from 'react';
import { ChatMessage } from '../../models/ChatMessage';

interface SSEProgressMessageProps {
  message: ChatMessage;
}

const SSEProgressMessage: React.FC<SSEProgressMessageProps> = ({ message }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { sseEventType, eventData } = message;

  const toggleExpansion = () => {
    setIsExpanded(!isExpanded);
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'success':
      case 'completed': return 'bg-green-500';
      case 'error':
      case 'failed': return 'bg-red-500';
      case 'processing':
      case 'in_progress': return 'bg-blue-500';
      case 'stopped': return 'bg-gray-400';
      default: return 'bg-yellow-500';
    }
  };

  const getStatusBgColor = (status?: string) => {
    switch (status) {
      case 'success':
      case 'completed': return 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800';
      case 'error':
      case 'failed': return 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800';
      case 'processing':
      case 'in_progress': return 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800';
      case 'stopped': return 'bg-gray-50 border-gray-200 dark:bg-gray-800/60 dark:border-gray-600';
      default: return 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800';
    }
  };

  const getToolDescription = () => {
    const description = eventData?.details?.args?.description;

    return typeof description === 'string' && description.trim().length > 0
      ? description.trim()
      : null;
  };

  const getFriendlyName = () => {
    if (sseEventType === 'tool_call') {
      const toolDescription = getToolDescription();

      return toolDescription || eventData?.details?.toolName || eventData?.message || 'Running Tool';
    }

    return eventData?.message || 'Processing';
  };

  const getDisplayMessage = () => {
    if (sseEventType === 'tool_call') {
      return getToolDescription() || message.content || eventData?.message || 'Processing...';
    }

    return message.content || eventData?.message || 'Processing...';
  };

  const getAlignment = () => {
    // All current SSE progress events are server-side.
    return 'justify-start';
  };

  const getExecutionBadge = () => {
    return null;
  };

  return (
    <div className={`flex ${getAlignment()}`}>
      <div className="max-w-full">
        <div
          className="cursor-pointer px-2 py-0.5 transition-colors"
          onClick={toggleExpansion}
        >
          <div className={`flex items-start gap-2 ${isExpanded ? 'mb-2' : ''}`}>
            <div className="mt-1 flex items-center gap-1">
              <div className={`w-2 h-2 rounded-full ${getStatusColor(eventData?.status)}`} />
            </div>
            <span className="text-xs font-medium leading-5 text-gray-600 dark:text-gray-300">
              {getFriendlyName()}
            </span>
            {getExecutionBadge()}
            <svg
              className={`mt-0.5 h-4 w-4 shrink-0 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>

          {/* Expanded content */}
          {isExpanded && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600 space-y-4">
              <div className={`p-3 rounded border ${getStatusBgColor(eventData?.status)}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-2 h-2 rounded-full ${getStatusColor(eventData?.status)}`} />
                  <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
                    {sseEventType === 'tool_call' ? 'Tool Execution' : 'Processing'}
                  </span>
                </div>

                {/* Content */}
                <div className="mb-2">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Message:</div>
                  <div className="text-xs bg-white dark:bg-gray-800 p-2 rounded border">
                    {getDisplayMessage()}
                  </div>
                </div>

                {/* Event Data */}
                {eventData && (
                  <div className="mb-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Event Data:</div>
                    <pre className="text-xs bg-white dark:bg-gray-800 p-2 rounded border overflow-x-auto">
                      {JSON.stringify(eventData, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Progress Bar */}
                {eventData?.progress !== undefined && (
                  <div className="mb-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Progress: {eventData.progress}%</div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                      <div
                        className="bg-blue-600 h-1.5 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${eventData.progress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Status */}
                <div className="mt-2 text-xs">
                  <span className="text-gray-500 dark:text-gray-400">Status: </span>
                  <span className={`font-medium ${eventData?.status === 'success' || eventData?.status === 'completed' ? 'text-green-600 dark:text-green-400' :
                    eventData?.status === 'error' || eventData?.status === 'failed' ? 'text-red-600 dark:text-red-400' :
                      eventData?.status === 'stopped' ? 'text-gray-600 dark:text-gray-300' :
                      'text-blue-600 dark:text-blue-400'
                    }`}>
                    {eventData?.status === 'success' || eventData?.status === 'completed' ? 'Completed' :
                      eventData?.status === 'error' || eventData?.status === 'failed' ? 'Failed' :
                        eventData?.status === 'stopped' ? 'Stopped' :
                        'In Progress'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SSEProgressMessage;
